import { ChangeDetectorRef, Component, NgZone, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { FormFeedbackService } from '../../services/form-feedback.service';
import QRCode from 'qrcode';
import {
  AccountMfaMethod,
  AccountSecurityOverview,
  AccountService,
  TotpEnrollment
} from '../../services/account.service';
import { AuthService } from '../../services/auth.service';

type PendingSecurityAction =
  | { type: 'totp-setup' }
  | { type: 'email-setup' }
  | { type: 'disable-mfa'; method: AccountMfaMethod }
  | { type: 'regenerate-recovery-codes' }
  | { type: 'connect-google' }
  | { type: 'disconnect-google' };

@Component({
  selector: 'app-security-page',
  standalone: true,
  imports: [FormsModule, DatePipe, RouterLink, InfoTooltipComponent],
  templateUrl: './security-page.component.html',
  styleUrls: ['./security-page.component.scss']
})
export class SecurityPageComponent implements OnInit {
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly formFeedback = inject(FormFeedbackService);

  security: AccountSecurityOverview | null = null;
  isLoading = true;
  isBusy = false;
  securityToken = '';
  verificationPassword = '';
  verificationError = '';
  pendingSecurityAction: PendingSecurityAction | null = null;
  securityMessage = '';
  securityError = '';
  passwordForm = { currentPassword: '', newPassword: '', confirmPassword: '' };
  totpEnrollment: TotpEnrollment | null = null;
  totpQrDataUrl = '';
  totpCode = '';
  emailEnrollmentId = '';
  emailCode = '';
  recoveryCodes: string[] = [];

  constructor(
    private readonly accountService: AccountService,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.loadSecurity();
  }

  get canSubmitPasswordChange(): boolean {
    return this.passwordForm.currentPassword.length > 0
      && this.authService.isPasswordValid(this.passwordForm.newPassword)
      && this.passwordForm.newPassword === this.passwordForm.confirmPassword;
  }

  get totpMethod(): AccountMfaMethod | undefined {
    return this.security?.mfaMethods.find((method) => method.type === 'totp' && method.enabled);
  }

  get emailMethod(): AccountMfaMethod | undefined {
    return this.security?.mfaMethods.find((method) => method.type === 'email' && method.enabled);
  }

  loadSecurity(): void {
    this.isLoading = true;
    this.accountService.getSecurity().subscribe({
      next: (security) => this.runInAngular(() => {
        this.security = security;
        this.isLoading = false;
      }),
      error: (error) => this.runInAngular(() => {
        this.securityError = this.getErrorMessage(error, 'Could not load your account security settings.');
        this.isLoading = false;
      })
    });
  }

  confirmSecurityAction(form: HTMLFormElement): void {
    this.clearNotices();
    if (!this.pendingSecurityAction || this.isBusy) return;
    if (!this.verificationPassword) {
      this.verificationError = 'Enter your current password to continue.';
      this.formFeedback.showInvalidFields(form, ['securityReauthenticationPassword']);
      return;
    }

    this.isBusy = true;
    this.accountService.reauthenticateSecurity(this.verificationPassword).subscribe({
      next: (result) => this.runInAngular(() => {
        this.securityToken = result.securityToken;
        this.verificationPassword = '';
        this.verificationError = '';
        this.executePendingSecurityAction();
      }),
      error: (error) => this.runInAngular(() => {
        this.verificationError = this.getErrorMessage(error, 'Your password could not be verified.');
        this.isBusy = false;
        this.formFeedback.showInvalidFields(form, ['securityReauthenticationPassword']);
      })
    });
  }

  startTotpSetup(): void {
    this.requestSecurityAction({ type: 'totp-setup' });
  }

  private beginTotpSetup(): void {
    this.beginBusy();
    this.accountService.beginTotpEnrollment(this.securityToken).subscribe({
      next: async (enrollment) => {
        try {
          const dataUrl = await QRCode.toDataURL(enrollment.provisioningUri, {
            width: 232,
            margin: 1,
            errorCorrectionLevel: 'M',
            color: { dark: '#102a56', light: '#ffffff' }
          });
          this.runInAngular(() => {
            this.totpEnrollment = enrollment;
            this.totpQrDataUrl = dataUrl;
            this.totpCode = '';
            this.clearPendingSecurityAction();
            this.isBusy = false;
          });
        } catch {
          this.runInAngular(() => {
            this.securityError = 'The authenticator setup code could not be rendered.';
            this.isBusy = false;
          });
        }
      },
      error: (error) => this.runInAngular(() => {
        this.handleProtectedActionError(error, 'Could not start authenticator app setup.');
      })
    });
  }

  confirmTotpSetup(): void {
    if (!this.totpEnrollment || !this.totpCode || this.isBusy) return;
    this.beginBusy();
    this.accountService.confirmTotpEnrollment(this.securityToken, this.totpCode.replace(/\s/g, '')).subscribe({
      next: (result) => this.runInAngular(() => {
        this.totpEnrollment = null;
        this.totpQrDataUrl = '';
        this.totpCode = '';
        this.recoveryCodes = result.recoveryCodes;
        this.securityMessage = result.message;
        this.isBusy = false;
        this.loadSecurity();
      }),
      error: (error) => this.runInAngular(() => {
        this.securityError = this.getErrorMessage(error, 'That authenticator code could not be verified.');
        this.isBusy = false;
      })
    });
  }

  startEmailSetup(): void {
    this.requestSecurityAction({ type: 'email-setup' });
  }

  private beginEmailSetup(): void {
    this.beginBusy();
    this.accountService.startEmailMfaEnrollment(this.securityToken).subscribe({
      next: (result) => this.runInAngular(() => {
        this.emailEnrollmentId = result.enrollmentId;
        this.emailCode = '';
        this.securityMessage = 'A confirmation code was sent to your account email address.';
        this.clearPendingSecurityAction();
        this.isBusy = false;
      }),
      error: (error) => this.runInAngular(() => {
        this.handleProtectedActionError(error, 'Could not send an email verification code.');
      })
    });
  }

  confirmEmailSetup(): void {
    if (!this.emailEnrollmentId || !this.emailCode || this.isBusy) return;
    this.beginBusy();
    this.accountService.confirmEmailMfaEnrollment(this.securityToken, this.emailEnrollmentId, this.emailCode.replace(/\s/g, '')).subscribe({
      next: (result) => this.runInAngular(() => {
        this.emailEnrollmentId = '';
        this.emailCode = '';
        this.recoveryCodes = result.recoveryCodes;
        this.securityMessage = result.message;
        this.isBusy = false;
        this.loadSecurity();
      }),
      error: (error) => this.runInAngular(() => {
        this.securityError = this.getErrorMessage(error, 'That email code could not be verified.');
        this.isBusy = false;
      })
    });
  }

  disableMethod(method: AccountMfaMethod): void {
    if (this.isBusy || !confirm(`Remove ${method.label} from two-step verification?`)) return;
    this.requestSecurityAction({ type: 'disable-mfa', method });
  }

  private removeMfaMethod(method: AccountMfaMethod): void {
    this.beginBusy();
    this.accountService.disableMfaMethod(this.securityToken, method.publicId).subscribe({
      next: (message) => this.runInAngular(() => {
        this.securityMessage = message;
        this.recoveryCodes = [];
        this.clearPendingSecurityAction();
        this.isBusy = false;
        this.loadSecurity();
      }),
      error: (error) => this.runInAngular(() => {
        this.handleProtectedActionError(error, 'Could not remove this verification method.');
      })
    });
  }

  regenerateRecoveryCodes(): void {
    this.requestSecurityAction({ type: 'regenerate-recovery-codes' });
  }

  private generateRecoveryCodes(): void {
    this.beginBusy();
    this.accountService.regenerateRecoveryCodes(this.securityToken).subscribe({
      next: (recoveryCodes) => this.runInAngular(() => {
        this.recoveryCodes = recoveryCodes;
        this.securityMessage = 'New recovery codes are ready. The previous codes no longer work.';
        this.clearPendingSecurityAction();
        this.isBusy = false;
        this.loadSecurity();
      }),
      error: (error) => this.runInAngular(() => {
        this.handleProtectedActionError(error, 'Could not generate new recovery codes.');
      })
    });
  }

  connectGoogle(): void {
    this.requestSecurityAction({ type: 'connect-google' });
  }

  private beginGoogleConnect(): void {
    this.beginBusy();
    this.accountService.startGoogleLink(this.securityToken).subscribe({
      next: (authorizationUrl) => window.location.assign(authorizationUrl),
      error: (error) => this.runInAngular(() => {
        this.handleProtectedActionError(error, 'Could not start Google account linking.');
      })
    });
  }

  disconnectGoogle(): void {
    if (this.isBusy || !confirm('Disconnect Google sign-in from this account?')) return;
    this.requestSecurityAction({ type: 'disconnect-google' });
  }

  private removeGoogleConnection(): void {
    this.beginBusy();
    this.accountService.disconnectGoogle(this.securityToken).subscribe({
      next: (message) => this.runInAngular(() => {
        this.securityMessage = message;
        this.clearPendingSecurityAction();
        this.isBusy = false;
        this.loadSecurity();
      }),
      error: (error) => this.runInAngular(() => {
        this.handleProtectedActionError(error, 'Could not disconnect Google sign-in.');
      })
    });
  }

  changePassword(form: HTMLFormElement): void {
    this.clearNotices();
    if (this.isBusy) return;
    if (!this.canSubmitPasswordChange) {
      const invalidFields = [
        !this.passwordForm.currentPassword && 'securityCurrentPassword',
        !this.authService.isPasswordValid(this.passwordForm.newPassword) && 'securityNewPassword',
        this.passwordForm.newPassword !== this.passwordForm.confirmPassword && 'securityConfirmPassword'
      ].filter((field): field is string => Boolean(field));
      this.formFeedback.showInvalidFields(form, invalidFields);
      return;
    }

    this.isBusy = true;
    this.accountService.changePassword({
      currentPassword: this.passwordForm.currentPassword,
      newPassword: this.passwordForm.newPassword
    }).subscribe({
      next: () => this.runInAngular(() => {
        this.authService.logout();
        void this.router.navigate(['/login'], { queryParams: { notice: 'PASSWORD_CHANGED' } });
      }),
      error: (error) => this.runInAngular(() => {
        const message = this.getErrorMessage(error, 'Could not change your password.');
        if (this.errorStatus(error) === 400) {
          const fields = message.includes('Current password')
            ? ['securityCurrentPassword']
            : ['securityNewPassword', 'securityConfirmPassword'];
          this.formFeedback.showInvalidFields(form, fields);
        } else {
          this.securityError = message;
        }
        this.isBusy = false;
      })
    });
  }

  async copyRecoveryCodes(): Promise<void> {
    if (!this.recoveryCodes.length) return;
    try {
      await navigator.clipboard.writeText(this.recoveryCodes.join('\n'));
      this.securityMessage = 'Recovery codes copied. Store them in a password manager or another secure location.';
    } catch {
      this.securityError = 'Copy is unavailable in this browser. Save the displayed codes manually.';
    }
  }

  eventLabel(type: string): string {
    return type.replace(/[._]/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
  }

  get securityActionLabel(): string {
    switch (this.pendingSecurityAction?.type) {
      case 'totp-setup': return 'set up your authenticator app';
      case 'email-setup': return 'set up email verification';
      case 'disable-mfa': return `remove ${this.pendingSecurityAction.method.label}`;
      case 'regenerate-recovery-codes': return 'generate new recovery codes';
      case 'connect-google': return 'connect Google sign-in';
      case 'disconnect-google': return 'disconnect Google sign-in';
      default: return 'continue';
    }
  }

  closeSecurityConfirmation(): void {
    if (this.isBusy) return;
    this.clearPendingSecurityAction();
  }

  clearVerificationError(): void {
    this.verificationError = '';
  }

  private requestSecurityAction(action: PendingSecurityAction): void {
    this.clearNotices();
    if (this.isBusy) return;
    this.pendingSecurityAction = action;
    this.verificationPassword = '';
    this.verificationError = '';
    if (this.securityToken) {
      this.executePendingSecurityAction();
    }
  }

  private executePendingSecurityAction(): void {
    const action = this.pendingSecurityAction;
    if (!action) {
      this.isBusy = false;
      return;
    }

    switch (action.type) {
      case 'totp-setup': this.beginTotpSetup(); break;
      case 'email-setup': this.beginEmailSetup(); break;
      case 'disable-mfa': this.removeMfaMethod(action.method); break;
      case 'regenerate-recovery-codes': this.generateRecoveryCodes(); break;
      case 'connect-google': this.beginGoogleConnect(); break;
      case 'disconnect-google': this.removeGoogleConnection(); break;
    }
  }

  private handleProtectedActionError(error: unknown, fallback: string): void {
    this.isBusy = false;
    if (this.errorStatus(error) === 403) {
      this.securityToken = '';
      this.verificationPassword = '';
      this.verificationError = 'Your confirmation expired. Enter your current password to continue.';
      return;
    }

    this.clearPendingSecurityAction();
    this.securityError = this.getErrorMessage(error, fallback);
  }

  private clearPendingSecurityAction(): void {
    this.pendingSecurityAction = null;
    this.verificationPassword = '';
    this.verificationError = '';
  }

  private beginBusy(): void {
    this.clearNotices();
    this.isBusy = true;
  }

  private clearNotices(): void {
    this.securityMessage = '';
    this.securityError = '';
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    const candidate = (error as { error?: { error?: { message?: unknown }; message?: unknown } })?.error;
    const message = candidate?.error?.message ?? candidate?.message;
    return typeof message === 'string' && message.trim() ? message : fallback;
  }

  private errorStatus(error: unknown): number | undefined {
    const status = (error as { status?: unknown })?.status;
    return typeof status === 'number' ? status : undefined;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
